import { formatDate, roleLabel } from '@viq-admin/ui';
import { companyHref, conferenceHref, leadHref, userHref } from '../components/entityHrefs';
import { recentHref, type RecentItem, type RecentKind } from '../recent/recentItems';

/**
 * Matching and ranking for the console's header search, kept free of React so
 * the rules can be tested on their own (see __tests__/search/searchModel.test.ts).
 *
 * The search answers one question: "take me to company / user / conference X"
 * in as few keystrokes as possible. So it is a jump list, not a report:
 *
 *  - Results come back grouped by type (Companies, Users, Conferences), a few
 *    per group, best first. The group order is fixed so the operator's eye
 *    learns where each type lands.
 *  - A pasted id is the fastest route of all. A GUID that matches a loaded
 *    company, conference or user (by Entra oid) is the single "Best match";
 *    a GUID that matches nothing loaded is most likely a lead (the one entity
 *    the console cannot list cross-tenant), so it offers "Open lead".
 *  - An exact email is unambiguous, so that user is the "Best match" too.
 *
 * Text matching is accent- and case-insensitive. Every word of the query must
 * appear somewhere in the record ("acme bos" finds "Acme" in "Boston"), and a
 * record ranks by how well its NAME matches: whole name, then name prefix,
 * then the start of a word in it, then anywhere. Secondary fields (a user's
 * email, a conference's location) count, but below the name. A one-letter
 * query only matches at the start of a word: "a" anywhere matches nearly
 * everything and helps no one.
 */

export type SearchKind = RecentKind;

export interface SearchResult {
  /** Unique within one result list; also the DOM id suffix for aria-activedescendant. */
  key: string;
  kind: SearchKind;
  id: string;
  label: string;
  /** Secondary line: a user's email, company and role; a conference's place and dates. */
  description?: string;
  /** Status worth seeing before you open it ("Archived", "Inactive"). */
  tag?: string;
  href: string;
}

export type SearchGroupKey = 'best' | 'lead' | 'recent' | 'companies' | 'users' | 'conferences';

export interface SearchGroup {
  key: SearchGroupKey;
  title: string;
  results: SearchResult[];
}

export interface SearchCompany {
  id: string;
  name: string;
  archived?: boolean;
}

export interface SearchUser {
  oid: string;
  name?: string | null;
  email?: string | null;
  companyId?: string | null;
  companyName?: string | null;
  role?: string | null;
  deactivatedAt?: string | null;
}

export interface SearchConference {
  id: string;
  name: string;
  location?: string | null;
  startDate?: string | null;
  endDate?: string | null;
}

export interface SearchSources {
  /** Active and archived tenants; `archived` marks the latter. */
  companies: SearchCompany[];
  users: SearchUser[];
  conferences: SearchConference[];
}

/** How many results each type shows: enough to choose from, few enough to scan. */
export const RESULTS_PER_GROUP = 5;

const GUID_RE = /^\{?([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\}?$/i;

/** The GUID in `query` (lower-cased, braces stripped), or null when it isn't one. */
export function parseGuid(query: string): string | null {
  const m = GUID_RE.exec(query.trim());
  return m ? m[1].toLowerCase() : null;
}

/** Lower-case, strip accents, collapse whitespace: "  Zoë  Ltd" → "zoe ltd". */
export function normalize(text: string | null | undefined): string {
  return (text ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** Characters after which a new "word" starts, for word-prefix matches. */
const WORD_BREAK = /[\s.@\-_/(),&+]/;

function startsWord(hay: string, q: string): boolean {
  let from = hay.indexOf(q);
  while (from !== -1) {
    if (from === 0 || WORD_BREAK.test(hay[from - 1])) return true;
    from = hay.indexOf(q, from + 1);
  }
  return false;
}

/**
 * Score tiers. The name tiers sit above every secondary tier, so a record
 * whose name matches always outranks one that matched only on email or place.
 * The gaps leave room for the small status penalty below without letting it
 * reorder tiers.
 */
const NAME_EXACT = 100;
const NAME_PREFIX = 80;
const NAME_WORD = 60;
const NAME_ANYWHERE = 40;
const SECONDARY_PREFIX = 35;
const SECONDARY_WORD = 30;
const SECONDARY_ANYWHERE = 20;
const ALL_WORDS = 10;
/** Archived tenants and deactivated users sort after live ones of the same tier. */
const INACTIVE_PENALTY = 1;

function scoreName(name: string, q: string): number {
  if (!name) return 0;
  if (name === q) return NAME_EXACT;
  if (name.startsWith(q)) return NAME_PREFIX;
  if (startsWord(name, q)) return NAME_WORD;
  if (q.length > 1 && name.includes(q)) return NAME_ANYWHERE;
  return 0;
}

function scoreSecondary(field: string, q: string): number {
  if (!field) return 0;
  if (field.startsWith(q)) return SECONDARY_PREFIX;
  if (startsWord(field, q)) return SECONDARY_WORD;
  if (q.length > 1 && field.includes(q)) return SECONDARY_ANYWHERE;
  return 0;
}

/** Every word of a multi-word query appears (at a word start, or anywhere once 2+ letters). */
function allWordsMatch(haystack: string, words: string[]): boolean {
  return words.every((w) => (w.length > 1 ? haystack.includes(w) : startsWord(haystack, w)));
}

/**
 * Score one record: the best of its name match, its secondary-field matches,
 * and (for multi-word queries) "every word appears somewhere". 0 = no match.
 */
function scoreRecord(name: string, secondary: string[], q: string, words: string[]): number {
  const best = Math.max(scoreName(name, q), ...secondary.map((s) => scoreSecondary(s, q)));
  if (best > 0) return best;
  if (words.length > 1 && allWordsMatch([name, ...secondary].join(' '), words)) return ALL_WORDS;
  return 0;
}

function companyResult(c: SearchCompany): SearchResult {
  return {
    key: `company:${c.id}`,
    kind: 'company',
    id: c.id,
    label: c.name,
    tag: c.archived ? 'Archived' : undefined,
    href: companyHref(c.id),
  };
}

function userResult(u: SearchUser, companyNames: Map<string, string>): SearchResult {
  const company = u.companyName || (u.companyId ? companyNames.get(u.companyId) : undefined);
  const description = [u.email, company, u.role ? roleLabel(u.role) : undefined]
    .filter(Boolean)
    .join(' · ');
  return {
    key: `user:${u.oid}`,
    kind: 'user',
    id: u.oid,
    // Never a bare oid: an unnamed user reads as their email.
    label: u.name || u.email || 'User',
    description: description || undefined,
    tag: u.deactivatedAt ? 'Inactive' : undefined,
    href: userHref(u.oid),
  };
}

function conferenceDates(c: SearchConference): string | undefined {
  if (!c.startDate) return undefined;
  const start = formatDate(c.startDate);
  const end = c.endDate ? formatDate(c.endDate) : start;
  return start === end ? start : `${start} – ${end}`;
}

function conferenceResult(c: SearchConference): SearchResult {
  const description = [c.location, conferenceDates(c)].filter(Boolean).join(' · ');
  return {
    key: `conference:${c.id}`,
    kind: 'conference',
    id: c.id,
    label: c.name,
    description: description || undefined,
    href: conferenceHref(c.id),
  };
}

interface Ranked {
  score: number;
  label: string;
  result: SearchResult;
}

function topRanked(ranked: Ranked[], limit: number): SearchResult[] {
  return ranked
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score || a.label.localeCompare(b.label))
    .slice(0, limit)
    .map((r) => r.result);
}

function companyNameMap(companies: SearchCompany[]): Map<string, string> {
  return new Map(companies.map((c) => [c.id, c.name]));
}

/** The record a pasted GUID names, if any loaded list holds it. */
function guidMatch(guid: string, sources: SearchSources): SearchResult | null {
  const company = sources.companies.find((c) => c.id.toLowerCase() === guid);
  if (company) return companyResult(company);
  const conference = sources.conferences.find((c) => c.id.toLowerCase() === guid);
  if (conference) return conferenceResult(conference);
  const user = sources.users.find((u) => u.oid.toLowerCase() === guid);
  if (user) return userResult(user, companyNameMap(sources.companies));
  return null;
}

/**
 * Search every source for `query` and return the non-empty groups in display
 * order. An empty or whitespace query returns no groups (the caller shows
 * recent items instead — see `recentGroup`).
 */
export function searchConsole(
  query: string,
  sources: SearchSources,
  opts: { perGroup?: number } = {},
): SearchGroup[] {
  const perGroup = opts.perGroup ?? RESULTS_PER_GROUP;
  const q = normalize(query);
  if (!q) return [];

  const guid = parseGuid(query);
  if (guid) {
    const hit = guidMatch(guid, sources);
    if (hit) return [{ key: 'best', title: 'Best match', results: [hit] }];
    return [
      {
        key: 'lead',
        title: 'Lead',
        results: [
          {
            key: `lead:${guid}`,
            kind: 'lead',
            id: guid,
            label: `Open lead ${guid}`,
            description: 'No company, user or conference has this id',
            href: leadHref(guid),
          },
        ],
      },
    ];
  }

  const words = q.split(' ');
  const companyNames = companyNameMap(sources.companies);

  const exactEmailUser = q.includes('@')
    ? sources.users.find((u) => normalize(u.email) === q)
    : undefined;

  const companies = topRanked(
    sources.companies.map((c) => ({
      score:
        scoreRecord(normalize(c.name), [], q, words) - (c.archived ? INACTIVE_PENALTY : 0),
      label: c.name,
      result: companyResult(c),
    })),
    perGroup,
  );

  const users = topRanked(
    sources.users
      .filter((u) => u !== exactEmailUser)
      .map((u) => ({
        score:
          scoreRecord(normalize(u.name), [normalize(u.email)], q, words) -
          (u.deactivatedAt ? INACTIVE_PENALTY : 0),
        label: u.name || u.email || '',
        result: userResult(u, companyNames),
      })),
    perGroup,
  );

  const conferences = topRanked(
    sources.conferences.map((c) => ({
      score: scoreRecord(normalize(c.name), [normalize(c.location)], q, words),
      label: c.name,
      result: conferenceResult(c),
    })),
    perGroup,
  );

  const groups: SearchGroup[] = [];
  if (exactEmailUser) {
    groups.push({
      key: 'best',
      title: 'Best match',
      results: [userResult(exactEmailUser, companyNames)],
    });
  }
  if (companies.length) groups.push({ key: 'companies', title: 'Companies', results: companies });
  if (users.length) groups.push({ key: 'users', title: 'Users', results: users });
  if (conferences.length) {
    groups.push({ key: 'conferences', title: 'Conferences', results: conferences });
  }
  return groups;
}

/** The "Recently opened" group shown for an empty query, or none when nothing is recent. */
export function recentGroup(items: RecentItem[]): SearchGroup[] {
  if (!items.length) return [];
  return [
    {
      key: 'recent',
      title: 'Recently opened',
      results: items.map((item) => ({
        key: `recent:${item.kind}:${item.id}`,
        kind: item.kind,
        id: item.id,
        label: item.label,
        description: item.sublabel,
        href: recentHref(item),
      })),
    },
  ];
}

/** Results in keyboard order: groups top to bottom, each group's results in order. */
export function flattenGroups(groups: SearchGroup[]): SearchResult[] {
  return groups.flatMap((g) => g.results);
}
